import { IconChevronDown16 } from '@fabric-ds/icons/react';
import { useRef } from 'react';
import { FinnFull } from '~/components/icons/finn';
import { useIntersectionObserver } from '~/hooks/useIntersectionObserver';
import { useIsVisibleOrBelow } from '~/hooks/useIsVisibleOrBelow';
import { useScrollPosition } from '~/hooks/useScrollPosition';
import blinkScrollCrop from '~/images/blink-scroll3x-crop.webp';
import logoer from '~/images/logoer.png';
import sammendrag1 from '~/images/sammendrag1.png';
import sammendrag2 from '~/images/sammendrag2.png';
import { Arrow } from './icons/arrow';
import BlinkLogoOneLine from './icons/blinkLogoWide';
import { Channels } from './icons/Channels';
import { Norway } from './icons/norway';
import { ScrollPosition } from './Scroll';
import Odometer from './stats/Odometer';
import type { ReactNode } from 'react';

export default function USPv2() {
    useScrollPosition();

    const ref = useRef<HTMLDivElement | null>(null);
    const refUsp1 = useRef<HTMLDivElement | null>(null);
    const refUsp2 = useRef<HTMLDivElement | null>(null);
    const refUsp3 = useRef<HTMLDivElement | null>(null);
    const refUsp4 = useRef<HTMLDivElement | null>(null);
    const entry = useIntersectionObserver(ref, {});
    const isVisible = !!entry?.isIntersecting;

    return (
        <>
            <div className="relative overflow-hidden" ref={ref}>
                {isVisible && <ScrollPosition />}
                <div
                    className="fixed left-0 right-0 top-0 z-0 h-screen bg-cover bg-center bg-no-repeat transition-all duration-700 ease-in-out"
                    style={{
                        boxShadow: `inset 0 0 0 1000px rgba(25, 29, 58, ${0.9})`,
                    }}
                />

                <div className="bg-blue-alt-900 relative m-auto flex flex-col text-center text-gray-900">
                    <div className="fixed flex h-screen w-full flex-col items-center justify-center bg-transparent">
                        <div className="mb-32 w-full px-56">
                            <BlinkLogoOneLine className="mx-auto max-w-screen-sm" />
                        </div>
                        <div className="mb-96 flex flex-col flex-wrap items-center justify-center gap-x-16 gap-y-10 text-16 font-bold text-white md:flex-row md:text-22">
                            <div>En markedsføringstjeneste fra</div>
                            <FinnFull />
                        </div>
                        <ul className="list-inside list-disc text-16 font-bold text-green-50 md:text-22">
                            <li className="mb-10">
                                <span className="relative -left-10">Landets beste målgruppe?</span>
                            </li>
                            <li className="mb-10">
                                <span className="relative -left-10">All statistikk på ett sted!</span>
                            </li>
                            <li className="mb-10">
                                <span className="relative -left-10">Nesten hele Norge på én uke</span>
                            </li>
                            <li className="mb-10">
                                <span className="relative -left-10">30% flere besøkende</span>
                            </li>
                        </ul>
                        <div className="absolute bottom-64 flex flex-col items-center text-green-100">
                            <button
                                className="flex max-w-max flex-col items-center gap-6 rounded-8 p-8"
                                onClick={() => scrollToRef(refUsp1)}>
                                <div>Landets beste målgruppe?</div>
                                <IconChevronDown16 />
                            </button>
                        </div>
                    </div>
                    <div className="lens flex h-screen flex-col bg-green-50"></div>

                    <div className="bg-blue-alt-900 h-screen"></div>
                    <div className="bg-blue-alt-900 h-screen"></div>
                </div>
            </div>

            <USPEntry
                title="Landets beste målgruppe?"
                main="Helt unik data er det som gjør Blink magisk. Vi vet hvem som ser
        på boligannonser på FINN i dag, og hvilke boliger og områder de
        ser på. Dette bruker vi til å bygge målgrupper med personer som
        er interessert i akkurat din type bolig. Og det smarteste av
        alt; vi vet hvem i målgruppen som ikke har sett annonsen din på
        FINN enda. Da kan du føle deg trygg på at annonsen din blir sett
        av enda flere og av en skreddersydd målgruppe. "
                next="All statistikk på ett sted!"
                step={2}
                total={4}
                containerRef={refUsp1}
                nextRef={refUsp2}>
                {(isVisible) => {
                    return (
                        <>
                            <div className="border-circle relative z-10 flex h-full w-full items-center overflow-hidden rounded-full border-green-400 bg-white">
                                <div className="no-scrollbar w-circle-content mx-auto h-full overflow-y-scroll">
                                    <img className="z-0 mx-auto bg-white" alt="" src={blinkScrollCrop} />
                                </div>
                            </div>
                        </>
                    );
                }}
            </USPEntry>
            <USPEntry
                title="All statistikk på ett sted!"
                main="Både megler og boligselger får tilgang til en rapport som viser
                hvordan Blink-annonsen presterer i sanntid. Hold deg løpende
                oppdatert på hvor mange som besøker annonsen og hvilke kanaler
                de kommer fra."
                next="Nesten hele Norge på én uke"
                step={2}
                total={4}
                containerRef={refUsp2}
                nextRef={refUsp3}>
                {(isVisible) => {
                    return (
                        <>
                            <div className="border-circle relative z-10 flex h-full w-full items-center overflow-hidden rounded-full border-green-400 bg-white">
                                <img className="z-0 m-auto bg-white px-44" alt="" src={sammendrag1} />
                            </div>
                            <div
                                className={`border-circle absolute left-0 top-0 flex h-full w-full transform-gpu items-center overflow-hidden rounded-full border-green-400 bg-white transition-transform duration-700 ${
                                    isVisible ? 'translate-circle-left' : 'translate-circle-center'
                                }`}>
                                <img
                                    className={`z-0 m-auto transform-gpu bg-white px-44 transition-transform duration-700 ${
                                        isVisible ? 'rotate-0 ' : 'rotate-60'
                                    }`}
                                    alt=""
                                    src={sammendrag2}
                                />
                            </div>
                            <div
                                className={`border-circle absolute left-0 top-0 flex h-full w-full transform-gpu items-center overflow-hidden rounded-full border-green-400 bg-white transition-transform duration-700 ${
                                    isVisible ? 'translate-circle-right' : 'translate-circle-center'
                                }`}>
                                <img
                                    className={`z-0 m-auto transform-gpu bg-white px-44 transition-transform duration-700 ${
                                        isVisible ? 'rotate-0 ' : '-rotate-60'
                                    }`}
                                    alt=""
                                    src={logoer}
                                />
                            </div>
                        </>
                    );
                }}
            </USPEntry>
            <USPEntry
                title="Nesten hele Norge på én uke"
                main="Schibsteds kanaler når ut til hele 90 % av Norges befolkning på
        én uke. Med Blink blir boligannonsen din vist på nettsteder som
        VG, Aftenposten, Bergens Tidende og en hel rekke lokalaviser, i
        tillegg til Facebook og Instagram. Her er det med andre ord
        store sjanser for å finne boligkjøperen du leter etter."
                next="Flere besøkende"
                step={3}
                total={4}
                containerRef={refUsp3}
                nextRef={refUsp4}>
                {(isVisible) => {
                    return (
                        <div className="border-circle relative z-10 flex h-full w-full items-center rounded-full border-green-400 bg-white">
                            <Norway isVisible={isVisible} />
                            <div className="circle-font-size absolute right-28 flex items-center">
                                <Odometer count={isVisible ? 90 : 1} className="text-blue-alt-900" />
                                <span>%</span>
                            </div>
                        </div>
                    );
                }}
            </USPEntry>
            <USPEntry
                title="Flere besøkende"
                main="Om det lønner seg å bruke Blink, spør du? Ja, det kan vi
        virkelig påstå at det gjør. Våre tall viser at annonser med
        Blink i snitt får ca. 30 prosent flere besøkende enn annonser
        uten. Det kan jo være nesten 30 prosent flere potensielle
        boligkjøpere, det!"
                step={4}
                total={4}
                containerRef={refUsp4}>
                {(isVisible) => {
                    return (
                        <>
                            <div className="border-circle relative z-10 flex h-full w-full items-center rounded-full border-green-400 bg-white">
                                <Arrow isVisible={isVisible} />
                                <div className="absolute top-24 flex w-full md:top-64 ">
                                    <div className="circle-font-size mx-auto flex items-center">
                                        <span>+</span>
                                        <Odometer count={isVisible ? 30 : 11} className="text-blue-alt-900" />
                                        <span>%</span>
                                    </div>
                                </div>
                            </div>
                            <div
                                className={`border-circle absolute left-0 top-0 flex h-full w-full transform-gpu items-center overflow-hidden rounded-full border-green-400 bg-white transition-transform duration-700 ${
                                    isVisible ? 'translate-circle-left' : 'translate-circle-center'
                                }`}>
                                <Channels />
                            </div>
                            <div
                                className={`border-circle absolute left-0 top-0 flex h-full w-full transform-gpu items-center overflow-hidden rounded-full border-green-400 bg-white transition-transform duration-700 ${
                                    isVisible ? 'translate-circle-right' : 'translate-circle-center'
                                }`}>
                                <Channels />
                            </div>
                        </>
                    );
                }}
            </USPEntry>
        </>
    );
}

type EntryProps = {
    title?: string;
    main: string;
    next?: string;
    containerRef?: React.RefObject<HTMLDivElement>;
    nextRef?: React.RefObject<HTMLDivElement>;
    children: (isVisible: boolean) => ReactNode;
    step: number;
    total: number;
};

function USPEntry({ title, main, containerRef, next, nextRef, children, step, total }: EntryProps) {
    const ref = useRef<HTMLDivElement>(null);
    const isVisible = useIsVisibleOrBelow(ref);
    return (
        <div ref={containerRef} className="text-blue-alt-900 card relative z-0 bg-green-50 ">
            <div className="z-10 mx-auto pt-64">
                <div className="flex w-full gap-0  ">
                    <div ref={ref} className="w-circle h-circle relative mx-auto bg-green-50">
                        {children(isVisible)}
                    </div>
                </div>
                <div className="bg-green-50">
                    <div className="mx-24 flex max-w-screen-md flex-col items-center gap-6 text-center md:mx-auto md:gap-10">
                        <h2 className="usp-heading mb-20 mt-40">{title}</h2>
                        <p className="md:text-18 mb-80 md:font-bold">{main}</p>
                        <Steps step={step} total={total} />
                    </div>
                    {next && nextRef && (
                        <div className="mb-64 flex flex-col items-center">
                            <button
                                className="flex max-w-max flex-col items-center gap-6 rounded-8 p-8"
                                onClick={() => scrollToRef(nextRef)}>
                                <div>{next}</div>
                                <IconChevronDown16 />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

type StepsProps = {
    step: number;
    total: number;
};
export function Steps({ step, total }: StepsProps) {
    return (
        <div className="mx-auto mb-48 flex flex-row gap-8">
            {Array.from(Array(total).keys()).map((i) => (
                <div
                    key={i}
                    className={`inline-block h-16 rounded-full md:h-20 ${
                        i === step - 1 ? 'bg-blue-alt-900 w-16 md:w-20' : 'w-44 bg-green-400 md:w-56'
                    }`}
                />
            ))}
        </div>
    );
}

function scrollToRef(scrollToRef?: React.RefObject<HTMLDivElement>) {
    if (scrollToRef && scrollToRef.current) {
        scrollToRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
        });
    }
}
